import moment from 'moment';
import { Spinner } from '../../../components/common';
import { convertObjectToParams } from '../../../components/common/create-params';
import { ROUTE_PATH } from '../../../routes/paths';
import { EngagementReportFilters } from '../common/reports-constants';
import { EngagementDetail, EngagementEvent, useEngagementReport } from '../../../hooks/useEngagementReporting';

const engagementEventMap: Record<EngagementEvent, string> = {
  call_completed: 'Call Completed',
  client_resource_view: 'Program View',
  client_resource_impression: 'Program Impression',
  global_resource_view: 'Resource View',
  global_resource_impression: 'Resource Impression',
  provider_view: 'Provider View',
  provider_impression: 'Provider Impression',
  video_play: 'Video Play',
  audio_play: 'Audio Play',
};

interface EngagementDetailsReportProps {
  filters: EngagementReportFilters;
}

export const EngagementDetailsReport: React.FC<EngagementDetailsReportProps> = ({ filters }) => {
  const { data: engagementDetailsReport, isLoading } = useEngagementReport(filters);

  const formatEventType = (engagementEvent: EngagementEvent): string => {
    return engagementEventMap[engagementEvent] || 'Unknown';
  };

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">Engagements Details Report</h1>
      {isLoading && <Spinner />}
      {engagementDetailsReport?.data.map((event: EngagementDetail) => (
        <div
          key={event.id}
          className="text-gray-dark flex w-full justify-between space-x-2 space-y-3 rounded border-b-8 border-white bg-gray-200 pb-3 text-left"
        >
          <div className="w-1/4 select-none space-y-1 pl-3 pt-3">
            <p className="text-xs uppercase text-neutral-600">Event Type</p>
            <p className="text-sm font-bold text-black">{formatEventType(event.engagement_type)}</p>
            <p className="text-xs uppercase text-neutral-600">Time</p>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-bold text-black">
                {moment.utc(event.created_at).local().format('MMM DD, YYYY HH:mm:ss')}
              </p>
              <p className="text-xs font-bold text-neutral-600">({moment.tz(moment.tz.guess()).format('z')})</p>
            </div>
            <div className="flex items-end space-x-2">
              <p className="text-sm font-bold text-black"></p>
              <p className="text-sm text-neutral-600"></p>
            </div>
          </div>
          <div className="w-1/4 select-none space-y-1">
            {event.provider_name && (
              <>
                <p className="text-xs uppercase text-neutral-600">Provider</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.provider_name}</p>
                </div>
              </>
            )}
            {['provider_view', 'provider_impression'].includes(event.engagement_type) && (
              <>
                <p className="text-xs uppercase text-neutral-600">Plan</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.engagement_name}</p>
                </div>
              </>
            )}
            {['client_resource_view', 'client_resource_impression'].includes(event.engagement_type) && (
              <>
                <p className="text-xs uppercase text-neutral-600">Program</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.engagement_name}</p>
                </div>
              </>
            )}
            {['global_resource_view', 'global_resource_impression'].includes(event.engagement_type) && (
              <>
                <p className="text-xs uppercase text-neutral-600">Resource</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.engagement_name}</p>
                </div>
              </>
            )}
            {['video_play', 'audio_play'].includes(event.engagement_type) && (
              <>
                {event.engagement_name && (
                  <>
                    <p className="text-xs uppercase text-neutral-600">Experience Name</p>
                    <div className="flex">
                      <p className="text-sm font-bold capitalize text-black">{event.engagement_name}</p>
                    </div>
                  </>
                )}
                <p className="text-xs uppercase text-neutral-600">Playback Location</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.meta_data.playback_location}</p>
                </div>
              </>
            )}
            {event.engagement_type === 'call_completed' && (
              <>
                <p className="text-xs uppercase text-neutral-600">Call Type</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">{event.engagement_name}</p>
                </div>
                <p className="text-xs uppercase text-neutral-600">Duration</p>
                <div className="flex">
                  <p className="text-sm font-bold capitalize text-black">
                    {moment.utc(event.meta_data.call_duration! * 1000).format('HH:mm:ss')}
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="w-1/4 space-y-1">
            <p className="select-none text-xs uppercase text-neutral-600">Member</p>
            <p
              className="cursor-pointer text-sm font-bold text-black"
              onClick={() => {
                window.open(
                  ROUTE_PATH.MEMBERS_PROFILE +
                    convertObjectToParams({
                      callerRoleId: event.caller_role_id,
                    }),
                  '_blank',
                );
              }}
            >
              {event.member_name}
            </p>
            <p className="select-none text-xs uppercase text-neutral-600">Client</p>
            <p
              className="cursor-pointer text-sm font-bold text-black"
              onClick={() => {
                localStorage.setItem('clientid', event.client_id.toString());
                window.open(ROUTE_PATH.REPORTING, '_blank');
              }}
            >
              {event.client_name}
            </p>
          </div>
          {event.engagement_type === 'provider_impression' && (
            <div className="w-1/4 space-y-1">
              <p className="select-none text-xs uppercase text-neutral-600">Selected Call</p>
              <p className="cursor-pointer text-sm font-bold text-black">
                {event.meta_data['selected_call'] ? 'true' : 'false'}
              </p>
              <p className="select-none text-xs uppercase text-neutral-600">Selected Website</p>
              <p className="cursor-pointer text-sm font-bold text-black">
                {event.meta_data['selected_website'] ? 'true' : 'false'}
              </p>
            </div>
          )}
          {!(event.engagement_type === 'provider_impression') && (
            <div className="w-1/4 space-y-1">
              {event.listener_role_id && (
                <>
                  <p className="select-none text-xs uppercase text-neutral-600">Listener</p>
                  <p
                    className="cursor-pointer text-sm font-bold text-black"
                    onClick={() => {
                      window.open(
                        ROUTE_PATH.LISTENERS_PROFILE +
                          convertObjectToParams({
                            listenerRoleId: event.listener_role_id,
                            listenerId: event.listener_user_id,
                          }),
                        '_blank',
                      );
                    }}
                  >
                    {event.listener_name}
                  </p>
                </>
              )}
              {event.engagement_type === 'call_completed' && (
                <>
                  <p className="text-xs uppercase text-neutral-600">Listener Call Topics</p>
                  <p className="flex flex-wrap gap-1 pb-4 text-base text-black">
                    {event.listener_topic_tags ? (
                      event.listener_topic_tags.map((tag, index) => {
                        return (
                          <div
                            key={index}
                            className="bg-blue-primary w-content rounded-md px-1 py-0.5 text-sm font-bold text-white"
                          >
                            {tag}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-base font-bold text-black">N/A</p>
                    )}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
